import $ from 'jquery';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { makeStyles, Typography, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { round } from 'lodash';
import { useAiServices, useLanguage, useAuth, useAlert, useListing } from 'hooks';
import { HvButton, HvModal, HvSelect } from 'components';
import {
  setOrderParams,
  setNoMoreSteps,
  setShowDisclaimer,
  setCreatingOrder,
  setSelectedRoomType,
  setSelectedRoomStyle,
  setAIPromotionApplied,
  setNextAiStagingOrderIsFree,
} from 'state/slices/aiServices';
import { COLOR, ALERT, GST_QST_RATE, MODAL } from 'config/constants';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import { createAiStagingOrder } from 'apis';
import { fetchAiStagingOrder, hideModal } from 'state/actions';
import { images } from 'assets/images';
import {
  roomStyles,
  roomTypes,
  getTitle,
  getRoomType,
  getRoomStyleImage,
} from './stagingOptionsConfig';

const useStyles = makeStyles({
  infoBox: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    maxWidth: 1000,
    maxHeight: 500,
    overflowY: 'auto',
    '@media (min-width: 767px)': {
      overlowY: 'hidden',
    },
  },
  disclaimerBox: {
    borderRadius: 5,
    fontSize: 12,
    padding: 5,
    height: 'fit-content',
    display: 'flex',
    alignItems: 'center',
  },
  disclaimerContent: {
    display: 'flex',
    padding: '4px 0',
    fontSize: 12,
    alignItems: 'center',
  },
  step1: {
    display: 'flex',
    flexDirection: 'column',
    width: '40%',
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  step2DesktopView: {
    display: 'flex',
    flexDirection: 'column',
    width: '60%',
    alignItems: 'end',
    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
  step2MobileView: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    '@media (min-width: 767px)': {
      display: 'none',
    },
  },
  roomTypeOptions: {
    borderRadius: 5,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    padding: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '90%',
    '@media (max-width: 767px)': {
      width: '95%',
    },
  },
  roomStyleOptions: {
    borderRadius: 5,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    padding: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '90%',
    maxHeight: 350,
    overflowY: 'auto',
    '@media (max-width: 767px)': {
      width: '95%',
      height: '100%',
    },
  },
  selectedOption: {
    color: COLOR.LIGHT_BLUE,
    backgroundColor: COLOR.WHITE,
    '& img': {
      opacity: 1,
    },
  },
  styleItem: {
    marginBottom: 5,
    padding: 5,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    color: COLOR.BLACK,
    transition: 'color 0.5s ease',
    '& img': {
      borderRadius: 5,
    },
    '&:hover': {
      cursor: 'pointer',
      color: COLOR.LIGHT_BLUE,
    },
  },
  selectedStyle: {
    color: COLOR.LIGHT_BLUE,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
    border: `solid ${COLOR.LIGHT_BLUE} 2px`,
  },
  optionTitle: {
    fontWeight: 'bold',
    fontSize: 16,
    color: COLOR.LIGHT_BLUE,
    marginBottom: 10,
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  roomName: {
    fontSize: 12,
  },
  generateSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 767px)': {
      alignItems: 'center',
    },
  },
  yesGenerateButton: {
    color: 'white',
    '& p': {
      fontSize: 16,
      '@media (max-width: 767px)': {
        fontSize: 12,
      },
    },
    backgroundColor: COLOR.LIGHT_BLUE,
  },
  noCancelButton: {
    color: COLOR.LIGHT_BLUE,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    '& p': {
      fontSize: 16,
      '@media (max-width: 767px)': {
        fontSize: 12,
      },
    },
    backgroundColor: COLOR.WHITE,
  },
  disclaimerTitles: {
    marginTop: 10,
    fontFamily: 'FordAntenna-Medium',
    fontSize: 15,
    '@media (max-width: 767px)': {
      fontSize: 13,
    },
  },
  disclaimerSubtitles: {
    fontSize: 12,
  },
  control: {
    flex: '1 1',
    marginBottom: 20,

    '&:last-child': {
      marginBottom: 0,
    },
  },
  selectDropdown: {
    overflow: 'hidden',
  },
  table: {
    border: `solid ${COLOR.GREY} 1px`,
    padding: 10,
    borderRadius: 8,
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    '& p': {
      '@media (max-width: 767px)': {
        fontSize: 12,
      },
    },
  },
  textBox: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    marginTop: 10,
    '& p': {
      '@media (min-width: 767px)': {
        fontSize: 14,
      },
      fontSize: 10,
    },
  },
  image: {
    marginBottom: 10,
    objectFit: 'contain',
    width: '100%',
  },
  iconTextRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  twoColumns: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-between',
  },
  selectedOptionContent: {
    borderRadius: 8,
    border: `solid ${COLOR.LIGHT_BLUE} 1px`,
    padding: 5,
    color: COLOR.LIGHT_BLUE,
    fontFamily: 'FordAntenna-Medium',
    marginTop: 5,
  },
  discount: {
    color: COLOR.GREEN,
  },
  crossedText: {
    textDecoration: 'line-through',
    textDecorationColor: COLOR.RED,
    color: COLOR.DARK_GREY,
  },
  promotionText: {
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    fontSize: 10,
    color: COLOR.DARK_GREY,
    width: '80%',
    '@media (max-width: 767px)': {
      width: '100%',
      fontSize: 8,
    },
  },
});

function SelectStagingOptions() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const language = useLanguage();
  const classes = useStyles();
  const arr = window.location.pathname.split('/');
  const listing_token = arr[arr.length - 1];
  const { displayAlert } = useAlert();
  const { token } = useAuth();
  const { listing } = useListing(listing_token);
  const {
    selectedPicture,
    listing_id,
    currentStep,
    selectedService,
    is_AI_promotion_applied,
    nextAiStagingOrderIsFree,
    showDisclaimer,
    creatingOrder,
    selectedRoomType,
    selectedRoomStyle,
  } = useAiServices();

  const { price } = selectedService;
  const taxes = round(price * GST_QST_RATE, 2);
  const total = round(price + taxes, 2);

  const fmtRoomType = intl.formatMessage({ id: 'deliver.aiServices.roomType' });
  const fmtRoomStyle = intl.formatMessage({ id: 'deliver.aiServices.roomStyle' });
  const fmtInstructions = intl.formatMessage({ id: 'deliver.aiServices.stagingInstructions' });
  const fmtAreYouSure = intl.formatMessage({ id: 'areYouSure' });
  const fmtDisclaimer = intl.formatMessage({ id: 'deliver.aiServices.disclaimer' });
  const fmtYesGenerate = intl.formatMessage({ id: 'yes.generate' });
  const fmtNoCancel = intl.formatMessage({ id: 'no.cancel' });
  const fmtCreatingOrderTitle = intl.formatMessage({ id: 'deliver.aiServices.creatingOrderTitle' });
  const fmtCreatingOrderText = intl.formatMessage({ id: 'deliver.aiServices.creatingOrderText' });
  const fmtVirualStagingAI = intl.formatMessage({ id: 'deliver.aiServices.aiVirtualStaging' });
  const fmtTaxes = intl.formatMessage({ id: 'taxes' });
  const fmtSubtotal = intl.formatMessage({
    id: 'customShootingOffer.addNewShootingOffer.subtotal',
  });
  const fmtTotal = intl.formatMessage({ id: 'total' });
  const fmtOrder = intl.formatMessage({ id: 'order' });
  const fmtPromotion = intl.formatMessage({ id: 'promotion.mainTitle' });
  const fmtFreePromotionDescription = intl.formatMessage({
    id: 'deliver.aiServices.freePromotionDescription',
  });
  const fmtPromotionDescription = intl.formatMessage({
    id: 'deliver.aiServices.promotionDescription',
  });
  const fmtFree = intl.formatMessage({ id: 'shooting.create.labels.freeServiceOffer' });
  const fmtAreYouSureToContinue = intl.formatMessage({ id: 'areYouSureToContinue' });

  const roomTypeOptions = roomTypes.map((roomType) => {
    return {
      value: roomType.key,
      label: getTitle(roomType, language),
      icon: roomType.icon,
    };
  });

  useEffect(() => {
    dispatch(setNoMoreSteps(true));
    return () => {
      dispatch(setNoMoreSteps(false));
    };
  }, []);

  useEffect(() => {
    if (listing && listing.nextAiStagingOrderIsFree) {
      dispatch(setNextAiStagingOrderIsFree(true));
    }
  }, [listing]);
  const onSelectRoomType = (value: string) => {
    dispatch(setSelectedRoomType(value));
  };

  const getConfirmationText = () => {
    if (is_AI_promotion_applied && !nextAiStagingOrderIsFree) {
      return `${fmtAreYouSure} $${total}?`;
    }
    return `${fmtAreYouSureToContinue}`;
  };

  async function createOrder() {
    try {
      const orderData = {
        config: {
          room_type: selectedRoomType,
          style: selectedRoomStyle,
          type: 'staging',
        },
        image_url: selectedPicture.original_url,
      };
      dispatch(setOrderParams(orderData));
      dispatch(setAIPromotionApplied(true));
      const response = await createAiStagingOrder(
        token,
        [orderData],
        listing_id,
        selectedService.id,
      );
      if (!response || response.status !== 200) {
        if (response.status === 409) {
          displayAlert(
            ALERT.ERROR,
            intl.formatMessage({ id: 'deliver.aiServices.stagingOrderConflictError' }),
          );
        } else {
          displayAlert(
            ALERT.ERROR,
            intl.formatMessage({ id: 'deliver.aiServices.stagingOrderError' }),
          );
        }
        dispatch(setCreatingOrder(false));
        return;
      }
      dispatch(fetchAiStagingOrder(token, listing_id));
      dispatch(setCreatingOrder(false));
      dispatch(hideModal(MODAL.AI_SERVICES));
      displayAlert(
        ALERT.SUCCESS,
        intl.formatMessage({ id: 'deliver.aiServices.stagingOrderSuccess' }),
      );
      const element = $('.section.aiStagingOrderNavigator')[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.log('error', error);
      dispatch(fetchAiStagingOrder(token, listing_id));
      dispatch(setCreatingOrder(false));
    }
  }

  const dislaimerModalsButtons = () => {
    return (
      <>
        <HvButton
          className={classes.noCancelButton}
          onClick={() => {
            dispatch(setShowDisclaimer(false));
          }}
        >
          <Typography>{fmtNoCancel}</Typography>
        </HvButton>
        <HvButton
          onClick={() => {
            dispatch(setCreatingOrder(true));
            createOrder();
            dispatch(setShowDisclaimer(false));
          }}
          className={classes.yesGenerateButton}
        >
          <Typography>{fmtYesGenerate}</Typography>
        </HvButton>
      </>
    );
  };
  const step1 = () => {
    return (
      <div>
        <div className={classes.roomTypeOptions} style={{ marginBottom: 20 }}>
          <Typography className={classes.optionTitle}>{fmtRoomType}</Typography>
          <div>
            <HvSelect
              classes={{ rootClass: classes.control, dropdownClass: classes.selectDropdown }}
              options={roomTypeOptions}
              value={selectedRoomType}
              onChange={onSelectRoomType}
              showIcon
            />
          </div>
        </div>
        <div className={classes.roomStyleOptions}>
          <Typography className={classes.optionTitle}>{fmtRoomStyle}</Typography>
          {Object.keys(roomStyles).map((key) => {
            const roomStyle = roomStyles[key];
            return (
              <div
                key={key}
                className={classNames(
                  classes.styleItem,
                  selectedRoomStyle === key && classes.selectedStyle,
                )}
                onClick={() => {
                  dispatch(setSelectedRoomStyle(key));
                }}
              >
                <div>
                  <img
                    width='100%'
                    src={getRoomStyleImage(selectedRoomType, key)}
                    alt='room style'
                  />
                </div>
                <Typography>{getTitle(roomStyle, language)}</Typography>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  const step2 = () => {
    return (
      <div>
        <img
          className={classes.image}
          src={selectedPicture.thumbnailURL}
          alt={`${selectedPicture.thumbnailURL}`}
        />
        <div className={classes.generateSection}>
          {selectedRoomStyle && selectedRoomType ? (
            <div className={classes.textBox}>
              <div style={{ width: '45%' }}>
                <div className={classes.iconTextRow}>
                  <img
                    src={images.listing}
                    style={{ marginRight: 5 }}
                    width={20}
                    height={20}
                    alt='room type'
                  />
                  <Typography style={{ color: COLOR.DARK_GREY }}>{`${fmtRoomType}: `}</Typography>
                </div>
                <Typography className={classes.selectedOptionContent}>
                  {getRoomType(selectedRoomType, language)}
                </Typography>
              </div>
              <div style={{ width: '45%' }}>
                <div className={classes.iconTextRow}>
                  <img
                    src={images.pallete}
                    style={{ marginRight: 5 }}
                    width={20}
                    height={20}
                    alt='room style'
                  />
                  <Typography style={{ color: COLOR.DARK_GREY }}>{`${fmtRoomStyle}: `}</Typography>
                </div>
                <Typography className={classes.selectedOptionContent}>
                  {getTitle(roomStyles[selectedRoomStyle], language)}
                </Typography>
              </div>
            </div>
          ) : (
            <Typography className={classes.disclaimerBox}>
              <InfoRoundedIcon color='secondary' style={{ marginRight: 5 }} />
              {fmtInstructions}
            </Typography>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className={classes.infoBox} id='infoBox'>
      <Grid container>
        <Grid item xs={12} md={6} className={classes.step2MobileView}>
          {step2()}
        </Grid>
        <Grid item xs={12} md={5} className={classes.step1}>
          {step1()}
        </Grid>
        <Grid item xs={12} md={7} className={classes.step2DesktopView}>
          {step2()}
        </Grid>
      </Grid>
      {showDisclaimer && (
        <HvModal
          onClose={() => {
            return dispatch(setShowDisclaimer(false));
          }}
          buttons={dislaimerModalsButtons()}
        >
          <Typography className={classes.disclaimerTitles} style={{ marginBottom: 5 }}>
            {fmtOrder}
          </Typography>
          <div className={classes.table}>
            <div className={classes.infoItem}>
              <Typography>
                {`${fmtVirualStagingAI} (${getRoomType(selectedRoomType, language)}, ${getTitle(
                  roomStyles[selectedRoomStyle],
                  language,
                )})`}
              </Typography>
              <Typography>{`$${price}`}</Typography>
            </div>
            <hr />
            <div className={classes.infoItem}>
              <Typography>{fmtTaxes}</Typography>
              <Typography>{`$${taxes}`}</Typography>
            </div>
            <hr />
            <div className={classes.infoItem}>
              <Typography>{fmtSubtotal}</Typography>
              <Typography>{`$${total}`}</Typography>
            </div>
            <hr />
            {!is_AI_promotion_applied || nextAiStagingOrderIsFree ? (
              <div>
                <div className={classes.infoItem}>
                  <div className={classes.promotionText}>
                    <Typography>{fmtPromotion}</Typography>
                    <Typography className={classes.description}>
                      {!is_AI_promotion_applied
                        ? fmtFreePromotionDescription
                        : fmtPromotionDescription}
                    </Typography>
                  </div>
                  <Typography className={classes.discount}>{`-$${total}`}</Typography>
                </div>
                <hr />
              </div>
            ) : null}
            <div className={classes.infoItem}>
              <Typography>{fmtTotal}</Typography>
              <div className={classes.iconTextRow}>
                <Typography
                  className={
                    !is_AI_promotion_applied || nextAiStagingOrderIsFree
                      ? classes.crossedText
                      : null
                  }
                >
                  {`$${total}`}
                </Typography>
                {!is_AI_promotion_applied || nextAiStagingOrderIsFree ? (
                  <Typography style={{ marginLeft: 5 }}>{fmtFree}</Typography>
                ) : null}
              </div>
            </div>
          </div>
          <Typography className={classes.disclaimerContent}>
            <InfoRoundedIcon color='secondary' style={{ marginRight: 5 }} />
            {fmtDisclaimer}
          </Typography>
          <Typography className={classes.disclaimerTitles} style={{ marginTop: 20 }}>
            {getConfirmationText()}
          </Typography>
        </HvModal>
      )}
    </div>
  );
}

export default SelectStagingOptions;
